import './boss-bar.css';

import { SHEFIRA_THRESHOLDS } from '@shefira/shared';

import { type AerieBarInfo, aerieBarLine } from './aerie-text.js';
import { uiFormat, uiText } from './text.js';

/**
 * The boss's health, large at the top of the screen, while the player stands
 * in its lair or fights it (ADR 0024): the plate over its head is too small to
 * read the thresholds by. Only a view of the synchronised state; lets every
 * click through to the world.
 */
export interface BossBarState {
  name: string;
  hp: number;
  maxHp: number;
  /** Past the quarter of its health: it strikes faster. */
  enraged: boolean;
  /**
   * Whether its loot of the day is still this player's to take (ADR 0090);
   * left out where no such loot exists (Shefira).
   */
  bonus?: boolean;
  /** Dead: when it stands up again, by `Date.now()`, for the countdown (ADR 0090). */
  wakesAt?: number;
  /** Shefira (ADR 0068): her three phases marked on the track, and her fight's line. */
  aerie?: AerieBarInfo;
}

export class BossBar {
  private readonly root: HTMLElement;
  private readonly name: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly value: HTMLElement;
  private readonly bonus: HTMLElement;
  private readonly marks: HTMLElement;
  private readonly line: HTMLElement;
  private shown: BossBarState | null = null;
  private countdown: number | undefined;

  constructor() {
    this.root = document.createElement('div');
    this.root.className = 'boss-bar';
    this.root.hidden = true;
    this.root.setAttribute('role', 'status');
    this.name = document.createElement('div');
    this.name.className = 'boss-bar__name';
    const track = document.createElement('div');
    track.className = 'boss-bar__track';
    this.fill = document.createElement('div');
    this.fill.className = 'boss-bar__fill';
    this.value = document.createElement('div');
    this.value.className = 'boss-bar__value';
    // Where her phases change (ADR 0068): the sky at 60 %, her rage at 30 %.
    this.marks = document.createElement('div');
    this.marks.className = 'boss-bar__marks';
    for (const share of [SHEFIRA_THRESHOLDS.flight, SHEFIRA_THRESHOLDS.enraged]) {
      const mark = document.createElement('span');
      mark.className = 'boss-bar__mark';
      mark.style.left = `${String(share * 100)}%`;
      this.marks.append(mark);
    }
    this.marks.hidden = true;
    track.append(this.fill, this.marks, this.value);
    this.bonus = document.createElement('div');
    this.bonus.className = 'boss-bar__bonus';
    this.line = document.createElement('div');
    this.line.className = 'boss-bar__line';
    this.line.hidden = true;
    this.root.append(this.name, track, this.bonus, this.line);
    document.body.append(this.root);
  }

  /** The boss to show, or null to hide the bar. Cheap to call every frame. */
  show(state: BossBarState | null): void {
    const was = this.shown;
    if (
      was?.name === state?.name &&
      was?.hp === state?.hp &&
      was?.maxHp === state?.maxHp &&
      was?.enraged === state?.enraged &&
      was?.bonus === state?.bonus &&
      was?.wakesAt === state?.wakesAt &&
      JSON.stringify(was?.aerie) === JSON.stringify(state?.aerie)
    ) {
      return;
    }
    this.shown =
      state === null ? null : { ...state, ...(state.aerie && { aerie: { ...state.aerie } }) };
    this.root.hidden = state === null;
    this.stopCountdown();
    if (state === null) return;
    this.name.textContent = uiText(state.name);
    const share = state.maxHp > 0 ? Math.max(0, Math.min(1, state.hp / state.maxHp)) : 0;
    this.fill.style.width = `${String(Math.round(share * 1000) / 10)}%`;
    this.value.textContent = `${String(state.hp)} / ${String(state.maxHp)}`;
    this.root.classList.toggle('boss-bar--enraged', state.enraged);
    this.root.classList.toggle('boss-bar--waking', state.wakesAt !== undefined);
    if (state.wakesAt !== undefined) this.startCountdown(state.wakesAt);
    this.bonus.hidden = state.bonus === undefined;
    this.bonus.classList.toggle('boss-bar__bonus--taken', state.bonus === false);
    this.bonus.textContent = uiText(
      state.bonus === false ? 'Butin du jour déjà pris' : 'Butin du jour à prendre',
    );
    const aerie = state.aerie;
    this.marks.hidden = aerie === undefined;
    this.line.hidden = aerie === undefined;
    this.root.classList.toggle('boss-bar--airborne', aerie?.airborne === true);
    if (aerie !== undefined) this.line.textContent = aerieBarLine(aerie);
  }

  /** The seconds left, ticked here: the scene redraws the bar only when the state moves. */
  private startCountdown(wakesAt: number): void {
    const tick = (): void => {
      const seconds = Math.max(0, Math.ceil((wakesAt - Date.now()) / 1000));
      this.value.textContent = uiFormat('De retour dans {seconds} s', { seconds });
    };
    tick();
    this.countdown = window.setInterval(tick, 250);
  }

  private stopCountdown(): void {
    if (this.countdown !== undefined) window.clearInterval(this.countdown);
    this.countdown = undefined;
  }

  destroy(): void {
    this.stopCountdown();
    this.root.remove();
  }
}
